import React, { useEffect, useState } from 'react';
import { api, formatApiError } from '../lib/api';
import { Plus, Trash2, KeyRound } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { Modal } from '../lib/Modal';
import { isAtPlanLimit, planUsageLabel } from '../lib/plans';
import { roleLabel } from '../lib/roleLabels';
import { PasswordStrength, isPasswordValid } from '../components/PasswordStrength';

export default function StaffPage() {
    const { user, restaurant, refresh } = useAuth();
    const [staff, setStaff] = useState([]);
    const [planFeatures, setPlanFeatures] = useState(restaurant?.plan_features || null);
    const [showAdd, setShowAdd] = useState(false);
    const [showPin, setShowPin] = useState(null);
    const [pin, setPin] = useState('');
    const [pinError, setPinError] = useState('');
    const [form, setForm] = useState({ name: '', email: '', password: '', role: 'waiter', phone: '' });
    const reload = async ({ syncAuth = false } = {}) => {
        const { data } = await api.get('/r/staff');
        setStaff(data.items);
        if (data.plan_features) setPlanFeatures(data.plan_features);
        if (syncAuth) await refresh({ silent: true });
    };
    useEffect(() => { reload(); }, []);

    const create = async () => {
        try {
            await api.post('/r/staff', form);
            setShowAdd(false); setForm({ name: '', email: '', password: '', role: 'waiter', phone: '' });
            reload({ syncAuth: true });
        } catch (e) { alert(formatApiError(e)); }
    };

    const remove = async (id) => {
        if (window.confirm('Ștergi utilizator?')) {
            await api.delete(`/r/staff/${id}`);
            reload({ syncAuth: true });
        }
    };

    const savePin = async () => {
        setPinError('');
        try {
            await api.put(`/r/staff/${showPin.id}/pin`, { pin });
            setShowPin(null); setPin(''); reload();
        } catch (e) { setPinError(formatApiError(e)); }
    };

    const staffAtLimit = isAtPlanLimit({ plan_features: planFeatures }, 'staff');
    const planCtx = { plan_features: planFeatures };

    return (
        <div className="fade-in-up" data-testid="staff-page">
            <div className="page-header">
                <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="kicker">Echipă</div>
                    <h1 className="headline page-title">Cine servește, cine gătește.</h1>
                    {planFeatures && (
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 6 }}>
                            Utilizatori: {planUsageLabel(planCtx, 'staff')}
                        </div>
                    )}
                    {restaurant?.staff_login_id && (
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 6 }}>
                            Cod login restaurant: <strong style={{ fontFamily: 'monospace', letterSpacing: '0.08em', color: 'var(--text)' }}>{restaurant.staff_login_id}</strong>
                        </div>
                    )}
                </div>
                <button type="button" className="btn btn-primary" onClick={() => setShowAdd(true)} disabled={staffAtLimit} data-testid="add-staff">
                    <Plus size={16} /> Adaugă
                </button>
            </div>

            {/* Desktop table (hidden < 760px) */}
            <div className="panel staff-table-wrap" style={{ padding: 0, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ background: 'rgba(255,255,255,0.02)' }}>
                            {['Nume', 'Email', 'Rol', 'ID login', 'Mese', 'PIN', 'Acțiuni'].map(h => <th key={h} style={{ padding: '0.8rem 1rem', textAlign: 'left', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>{h}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {staff.map(s => (
                            <tr key={s.id} style={{ borderTop: '1px solid var(--border)' }} data-testid={`staff-${s.id}`}>
                                <td style={{ padding: '0.85rem 1rem' }}>{s.name}</td>
                                <td style={{ padding: '0.85rem 1rem', color: 'var(--text-muted)' }}>{s.email}</td>
                                <td style={{ padding: '0.85rem 1rem' }}><span className="badge badge-amber">{roleLabel(s.role)}</span></td>
                                <td style={{ padding: '0.85rem 1rem', fontFamily: 'monospace', letterSpacing: '0.08em' }}>
                                    {['waiter', 'kitchen', 'manager'].includes(s.role) ? (s.staff_code || '—') : '—'}
                                </td>
                                <td style={{ padding: '0.85rem 1rem', color: 'var(--text-muted)' }}>
                                    {s.role === 'waiter' ? (s.assigned_tables_count ?? 0) : '—'}
                                </td>
                                <td style={{ padding: '0.85rem 1rem' }}>
                                    {['waiter', 'kitchen', 'manager'].includes(s.role) ? (
                                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setShowPin(s); setPin(''); setPinError(''); }} data-testid={`set-pin-${s.id}`}>
                                            <KeyRound size={13} /> {s.has_pin ? 'Schimbă' : 'Setează'}
                                        </button>
                                    ) : <span style={{ color: 'var(--text-faint)' }}>—</span>}
                                </td>
                                <td style={{ padding: '0.85rem 1rem' }}>
                                    {s.id !== user?.id && (
                                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => remove(s.id)}><Trash2 size={14} color="var(--neon-magenta)" /></button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile cards (hidden >= 760px) */}
            <div className="staff-cards" data-testid="staff-cards">
                {staff.map(s => (
                    <div key={s.id} className="panel staff-card" data-testid={`staff-card-${s.id}`}>
                        <div className="staff-card__head">
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div className="staff-card__name">{s.name}</div>
                                <div className="staff-card__email">{s.email}</div>
                            </div>
                            <span className="badge badge-amber" style={{ flexShrink: 0 }}>{roleLabel(s.role)}</span>
                        </div>
                        <div className="staff-card__meta">
                            {['waiter', 'kitchen', 'manager'].includes(s.role) && (
                                <div className="staff-card__row">
                                    <span className="staff-card__label">ID login</span>
                                    <span style={{ fontFamily: 'monospace', letterSpacing: '0.08em' }}>{s.staff_code || '—'}</span>
                                </div>
                            )}
                            {s.role === 'waiter' && (
                                <div className="staff-card__row">
                                    <span className="staff-card__label">Mese asignate</span>
                                    <span>{s.assigned_tables_count ?? 0}</span>
                                </div>
                            )}
                        </div>
                        <div className="staff-card__actions">
                            {['waiter', 'kitchen', 'manager'].includes(s.role) && (
                                <button type="button" className="btn btn-secondary btn-sm" style={{ flex: 1 }}
                                        onClick={() => { setShowPin(s); setPin(''); setPinError(''); }}
                                        data-testid={`set-pin-mobile-${s.id}`}>
                                    <KeyRound size={14} /> {s.has_pin ? 'Schimbă PIN' : 'Setează PIN'}
                                </button>
                            )}
                            {s.id !== user?.id && (
                                <button type="button" className="btn btn-ghost btn-sm" onClick={() => remove(s.id)}>
                                    <Trash2 size={14} color="var(--neon-magenta)" />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
                {!staff.length && (
                    <div className="panel" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                        Niciun angajat. Adaugă primul cu butonul de sus.
                    </div>
                )}
            </div>

            <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Membru nou" titleId="staff-add-title">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                    <div><label className="label">Nume</label><input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="staff-name" /></div>
                    <div><label className="label">Email</label><input className="input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="staff-email" /></div>
                    <div>
                        <label className="label">Parolă inițială</label>
                        <input className="input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="staff-password" />
                        <PasswordStrength value={form.password} />
                    </div>
                    <div><label className="label">Rol</label>
                        <select className="input" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} data-testid="staff-role">
                            <option value="waiter">Ospătar</option>
                            <option value="kitchen">Bucătărie</option>
                            <option value="manager">Manager</option>
                        </select>
                    </div>
                    <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.4rem' }}>
                        <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowAdd(false)}>Anulează</button>
                        <button type="button" className="btn btn-primary" style={{ flex: 1 }} onClick={create} disabled={!form.name || !form.email || !isPasswordValid(form.password)} data-testid="staff-create">Creează</button>
                    </div>
                </div>
            </Modal>

            <Modal open={!!showPin} onClose={() => setShowPin(null)} title={showPin ? `PIN pentru ${showPin.name}` : ''} maxWidth={380}>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', marginTop: 0 }}>
                    Exact 6 cifre. Folosit împreună cu codul restaurantului și ID-ul angajat ({showPin?.staff_code || '—'}).
                </p>
                <input className="input" type="password" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                       value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                       placeholder="••••••" style={{ fontSize: '1.4rem', letterSpacing: '0.4em', textAlign: 'center' }}
                       data-testid="staff-pin-input" autoFocus />
                {pinError && <div style={{ color: 'var(--neon-magenta)', fontSize: '0.85rem', marginTop: 8 }} data-testid="staff-pin-error">{pinError}</div>}
                <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1rem' }}>
                    <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowPin(null)}>Anulează</button>
                    <button type="button" className="btn btn-primary" style={{ flex: 1 }}
                            disabled={pin.length !== 6} onClick={savePin} data-testid="staff-pin-save">Salvează PIN</button>
                </div>
            </Modal>
        </div>
    );
}
